import { useEffect, useRef, useState } from 'react';
import { api, fmt, fmtDate, getUser } from '../api';
import { Modal, useToast, Loading, Empty, Eyebrow, ErrorBox, rowNav } from '../ui';
import { Icons } from '../icons';

export default function Receipts() {
  const [list, setList] = useState<any[] | null>(null);
  const [err, setErr] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [creating, setCreating] = useState(false);
  const [view, setView] = useState<any>(null);
  const [openOrders, setOpenOrders] = useState<any[]>([]);
  const [selectedOrders, setSelectedOrders] = useState<number[]>([]);
  const [positions, setPositions] = useState<any[]>([]);
  const [extra, setExtra] = useState<any[]>([]); // zakazsiz kelgan tovarlar
  const [extraSearch, setExtraSearch] = useState('');
  const [extraResults, setExtraResults] = useState<any[]>([]);
  const [editItems, setEditItems] = useState<any[] | null>(null);
  const reqSeq = useRef(0); // javoblar poygasi himoyasi
  const xlsRef = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const user = getUser();
  const canEdit = ['admin', 'zakaz_operator', 'rastamojka'].includes(user?.role);


  const load = () => { setErr(''); setList(null); api.get('/receipts').then(setList).catch((e) => { setErr(e.message); setList([]); }); };
  useEffect(() => { load(); }, []);

  // Ro'yxat filtri (raqam / zakaz raqami / sana oralig'i)
  const filtered = (list || []).filter((r) => {
    const s = search.trim().toLowerCase();
    if (s) {
      const hay = (r.number + ' ' + r.orders.map((o: any) => o.order.number).join(' ')).toLowerCase();
      if (!hay.includes(s)) return false;
    }
    const d = new Date(r.date);
    if (dateFrom && d < new Date(dateFrom)) return false;
    if (dateTo && d > new Date(dateTo + 'T23:59:59')) return false;
    return true;
  });

  const startCreate = async () => {
    setCreating(true); setSelectedOrders([]); setPositions([]); setExtra([]); setExtraSearch(''); setExtraResults([]);
    try {
      const [transit, conf, partial] = await Promise.all([
        api.get('/orders?status=in_transit'),
        api.get('/orders?status=confirmed'),
        api.get('/orders?status=partial'),
      ]);
      setOpenOrders([...transit, ...conf, ...partial]);
    } catch (e: any) { toast(e.message, true); setOpenOrders([]); }
  };

  // Zakazsiz tovar qidirish (katalogdan)
  useEffect(() => {
    if (extraSearch.trim().length < 2) { setExtraResults([]); return; }
    const t = setTimeout(() => {
      api.get('/products?pageSize=8&search=' + encodeURIComponent(extraSearch.trim()))
        .then((d) => setExtraResults(d.items)).catch(() => setExtraResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [extraSearch]);
  const addExtra = (p: any) => {
    if (!extra.some((x) => x.productId === p.id)) setExtra([...extra, { productId: p.id, article: p.article, name: p.name, qty: '' }]);
    setExtraSearch(''); setExtraResults([]);
  };

  const toggleOrder = async (oid: number) => {
    const sel = selectedOrders.includes(oid) ? selectedOrders.filter((x) => x !== oid) : [...selectedOrders, oid];
    setSelectedOrders(sel);
    if (!sel.length) { setPositions([]); return; }
    const seq = ++reqSeq.current;
    const pos = await api.get('/receipts/open-positions?orderIds=' + sel.join(','));
    if (seq !== reqSeq.current) return; // eski javob — tashlab yuboriladi
    // oldin kiritilgan «Hozir keldi» qiymatlarini saqlab qolamiz
    setPositions((prev) => {
      const entered = new Map(prev.map((p) => [p.orderItemId, p.nowReceived]));
      return pos.map((p: any) => ({ ...p, nowReceived: entered.has(p.orderItemId) ? entered.get(p.orderItemId) : p.remaining }));
    });
  };
  /** Kelgan miqdorlarni Excel'dan o'qib, jadvalga qo'yadi (saqlanmaydi — ko'rib chiqasiz). */
  const loadExcel = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const r = await api.upload('/receipts/parse-excel', file, { orderIds: selectedOrders.join(',') });
      if (!r.matched.length) {
        toast('Fayldagi artikullar ulangan zakazlarda topilmadi', true);
      } else {
        const byId = new Map(r.matched.map((m: any) => [m.orderItemId, m.qty]));
        setPositions((prev) => prev.map((p) => (byId.has(p.orderItemId) ? { ...p, nowReceived: String(byId.get(p.orderItemId)) } : p)));
        const parts = [`${r.matched.length} qator to'ldirildi`];
        if (r.unknown.length) parts.push(`${r.unknown.length} artikul topilmadi`);
        if (r.skipped) parts.push(`${r.skipped} qator o'tkazildi`);
        toast(parts.join(' · '));
      }
    } catch (err: any) { toast(err.message, true); }
    if (xlsRef.current) xlsRef.current.value = '';
  };

  const fillAll = () => setPositions(positions.map((p) => ({ ...p, nowReceived: p.remaining })));

  const save = async () => {
    try {
      await api.post('/receipts', {
        orderIds: selectedOrders,
        items: [
          ...positions.filter((p) => Number(p.nowReceived) > 0).map((p) => ({ orderItemId: p.orderItemId, productId: p.productId, qtyReceived: p.nowReceived })),
          // zakazsiz kelgan tovarlar — orderItemId'siz
          ...extra.filter((x) => Number(x.qty) > 0).map((x) => ({ productId: x.productId, qtyReceived: x.qty })),
        ],
      });
      toast('Kirim saqlandi'); setCreating(false); load();
    } catch (e: any) { toast(e.message, true); }
  };
  const openView = (id: number) => api.get('/receipts/' + id).then((r) => { setView(r); setEditItems(null); }).catch((e) => toast(e.message, true));

  /** Xato kiritilgan miqdorni to'g'rilash — yetmovchiliklar qayta hisoblanadi. */
  const saveEdit = async () => {
    try {
      await api.patch('/receipts/' + view.id, {
        date: view.date, note: view.note,
        items: editItems!.map((i) => ({ id: i.id, qtyReceived: i.qtyReceived })),
      });
      toast('Kirim yangilandi'); setView(null); setEditItems(null); load();
    } catch (e: any) { toast(e.message, true); }
  };

  return (
    <div>
      <div className="page-head">
        <div>
          <h1 className="page-title">Kirim (Priyomka)</h1>
          <p className="page-sub">
            {list ? filtered.length : '—'} ta kirim hujjati · O'zbekistonga yetib kelgan yuklarni qabul qilish
          </p>
        </div>
        {canEdit && <button className="btn btn-primary" onClick={startCreate}><Icons.plus size={16} /> Yangi kirim</button>}
      </div>

      <div className="filters">
        <input className="search" placeholder="Kirim yoki zakaz raqami bo'yicha qidirish…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <input className="input" style={{ width: 150 }} type="date" title="Sanadan" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        <input className="input" style={{ width: 150 }} type="date" title="Sanagacha" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        {(search || dateFrom || dateTo) && <button className="btn" onClick={() => { setSearch(''); setDateFrom(''); setDateTo(''); }}>Tozalash</button>}
      </div>

      {err ? <ErrorBox text={err} onRetry={load} /> : !list ? <Loading /> : filtered.length === 0 ? (
        <Empty text={list.length ? 'Filtrga mos kirim yo\'q' : "Hali kirim yo'q"} hint={list.length ? undefined : "Yuk kelganda «+ Yangi kirim» bilan qabul qiling"} />
      ) : (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Raqam</th><th>Sana</th><th>Zakazlar</th><th className="num">Pozitsiyalar</th><th></th></tr></thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} style={{ cursor: 'pointer' }} onClick={rowNav(() => openView(r.id))}>
                  <td className="link code">{r.number}</td>
                  <td>{fmtDate(r.date)}</td>
                  <td>{r.orders.map((o: any) => o.order.number).join(', ')}</td>
                  <td className="num">{r._count.items}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {user?.role === 'admin' && (
                      <button className="act act-del" title="O'chirish" onClick={async () => {
                        if (!confirm(`${r.number} o'chirilsinmi? Yetmovchiliklar qayta hisoblanadi.`)) return;
                        try { await api.del('/receipts/' + r.id); toast("O'chirildi"); load(); } catch (e: any) { toast(e.message, true); }
                      }}><Icons.trash size={16} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {view && (
        <Modal title={`${view.number} — ${fmtDate(view.date)}`} onClose={() => { setView(null); setEditItems(null); }} large>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: -8 }}>
            <p className="muted" style={{ margin: 0 }}>
              Zakazlar: {view.orders.map((o: any) => o.order.number).join(', ')}
              {view.orders[0]?.order?.supplier?.name ? ` · ${view.orders[0].order.supplier.name}` : ''}
            </p>
            {canEdit && !editItems && view.items.length > 0 && (
              <button className="btn btn-sm" onClick={() => setEditItems(view.items.map((i: any) => ({ id: i.id, qtyReceived: Number(i.qtyReceived) })))}>
                <Icons.edit size={15} /> Miqdorni to'g'rilash
              </button>
            )}
          </div>
          {!view.items.length ? <Empty text="Bu hujjatda pozitsiya yo'q" /> : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Artikul</th><th>Nomi</th><th>Zakaz</th><th className="num">Kelgan miqdor</th><th>Turi</th></tr></thead>
                <tbody>
                  {view.items.map((it: any) => {
                    const ed = editItems?.find((x) => x.id === it.id);
                    return (
                      <tr key={it.id}>
                        <td className="code">{it.product?.article}</td>
                        <td>{it.product?.name}</td>
                        <td>{it.orderItem?.order?.number || '—'}</td>
                        <td className="num" style={{ fontWeight: 600 }}>
                          {ed ? (
                            <input className="input num cell-input" style={{ maxWidth: 110 }} type="number" min="0" value={ed.qtyReceived}
                              onChange={(e) => setEditItems(editItems!.map((x) => x.id === it.id ? { ...x, qtyReceived: e.target.value } : x))} />
                          ) : fmt(it.qtyReceived)}
                        </td>
                        <td>{it.isUnordered ? <span className="badge b-transit">zakazsiz</span> : <span className="badge b-received">zakaz bo'yicha</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {editItems && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14, alignItems: 'center' }}>
              <span className="muted" style={{ fontSize: 12, marginRight: 'auto' }}>0 qo'ysangiz qator olib tashlanadi</span>
              <button className="btn" onClick={() => setEditItems(null)}>Bekor</button>
              <button className="btn btn-primary" onClick={saveEdit}>Saqlash</button>
            </div>
          )}
        </Modal>
      )}

      {creating && (
        <Modal title="Yangi kirim hujjati" onClose={() => setCreating(false)} large>
          <Eyebrow title="1 · Zakazlarni ulash" desc="bitta konteynerda kelgan zakazlarni belgilang" />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
            {openOrders.length === 0 && <span className="muted">Ochiq zakaz yo'q — avval zakazni «Yo'lda» statusiga o'tkazing</span>}
            {openOrders.map((o) => (
              <label key={o.id} className={'chip' + (selectedOrders.includes(o.id) ? ' on' : '')}>
                <input type="checkbox" checked={selectedOrders.includes(o.id)} onChange={() => toggleOrder(o.id)} />
                {o.number} · {o.supplier?.name || '—'}
              </label>
            ))}
          </div>

          {positions.length > 0 && <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <Eyebrow title="2 · Kelgan miqdor" desc="farq bor qatorlarni to'g'rilang" />
              <div style={{ display: 'flex', gap: 8 }}>
                <input ref={xlsRef} type="file" accept=".xlsx" hidden onChange={loadExcel} />
                <button className="btn btn-sm" onClick={() => xlsRef.current?.click()} title="Faylda «Artikul» va «Kelgan miqdor» ustunlari bo'lsa yetarli">
                  ⬆ Excel'dan yuklash
                </button>
                <button className="btn btn-sm" onClick={fillAll}>Hammasini zakaz bo'yicha to'ldirish</button>
              </div>
            </div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Artikul</th><th>Nomi</th><th>Zakaz</th><th className="num">Zakaz</th><th className="num">Avval</th><th className="num">Hozir keldi</th><th className="num">Qoldiq</th><th>Holat</th></tr></thead>
                <tbody>
                  {positions.map((p, i) => {
                    const now = Number(p.nowReceived) || 0;
                    const remain = p.ordered - p.received - now;
                    const st = remain <= 0 ? (remain < 0 ? ['➕ Ortiqcha', 'b-confirmed'] : ["✅ To'liq", 'b-received']) : now > 0 ? ['⚠ Qisman', 'b-partial'] : ['❌ Kelmadi', 'b-cancelled'];
                    return (
                      <tr key={p.orderItemId}>
                        <td>{p.article}</td><td>{p.name}</td><td>{p.orderNumber}</td>
                        <td className="num">{fmt(p.ordered)}</td><td className="num">{fmt(p.received)}</td>
                        <td className="num"><input className="input num cell-input" style={{ maxWidth: 100 }} type="number" min="0" value={p.nowReceived}
                          onChange={(e) => setPositions(positions.map((x, idx) => idx === i ? { ...x, nowReceived: e.target.value } : x))} /></td>
                        <td className="num">{fmt(remain)}</td>
                        <td><span className={'badge ' + st[1]}>{st[0]}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Eyebrow title="3 · Zakazsiz kelgan tovar" desc="zakazda yo'q, lekin konteynerda kelgan mahsulot (ixtiyoriy)" />
            <div className="card" style={{ padding: 12, marginBottom: 10 }}>
              <div style={{ position: 'relative' }}>
                <input className="search" style={{ width: '100%', maxWidth: '100%' }}
                  placeholder="Katalogdan qidiring: artikul yoki nom…"
                  value={extraSearch} onChange={(e) => setExtraSearch(e.target.value)} />
                {extraResults.length > 0 && (
                  <div className="card" style={{ position: 'absolute', top: 44, left: 0, right: 0, zIndex: 30, padding: 6, maxHeight: 260, overflowY: 'auto' }}>
                    {extraResults.map((p) => (
                      <div key={p.id} className="row-card" style={{ margin: 4, padding: '8px 12px', cursor: 'pointer' }} onClick={() => addExtra(p)}>
                        <div><div className="row-title">{p.name}</div><div className="row-sub">{p.article}</div></div>
                        <span className="btn btn-sm btn-primary">+ qo'shish</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {extra.length > 0 && (
                <table style={{ marginTop: 10 }}>
                  <thead><tr><th>Artikul</th><th>Nomi</th><th className="num">Kelgan miqdor</th><th></th></tr></thead>
                  <tbody>
                    {extra.map((x, i) => (
                      <tr key={x.productId}>
                        <td>{x.article}</td><td>{x.name}</td>
                        <td className="num"><input className="input num cell-input" style={{ maxWidth: 100 }} type="number" min="0" value={x.qty}
                          onChange={(e) => setExtra(extra.map((y, idx) => idx === i ? { ...y, qty: e.target.value } : y))} /></td>
                        <td><button className="act act-del" onClick={() => setExtra(extra.filter((_, idx) => idx !== i))}><Icons.trash size={15} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
              <button className="btn" onClick={() => setCreating(false)}>Bekor</button>
              <button className="btn btn-primary" onClick={save}>Kirimni saqlash</button>
            </div>
          </>}
        </Modal>
      )}
    </div>
  );
}
